import { useState, useRef } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';

import { Button } from '@components/Button';
import { Input } from '@components/Input';

/**
 * Payment Form Component
 * Integrates with Square Web Payments SDK
 * 
 * Note: This is a simplified version. For production, you'll need to:
 * 1. Load Square Web Payments SDK script
 * 2. Initialize Square payment form
 * 3. Handle payment tokenization
 * 4. Process payment through Cloud Function
 */

interface PaymentFormProps {
  amount: number; // Amount in cents
  onPaymentSuccess: (paymentId: string, sourceId: string) => void;
  onPaymentError: (error: string) => void;
  isLoading?: boolean;
}

export function PaymentForm({
  amount,
  onPaymentSuccess,
  onPaymentError,
  isLoading = false,
}: PaymentFormProps): JSX.Element {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // For now, this is a mock form
  // In production, replace with Square Web Payments SDK
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isProcessing || isLoading) return;

    // Basic validation
    if (!cardNumber || !expiryDate || !cvv || !cardholderName || !zipCode) {
      onPaymentError('Please fill in all payment fields');
      return;
    }

    setIsProcessing(true);

    try {
      // TODO: Replace with actual Square Web Payments SDK tokenization
      // For now, simulate payment tokenization
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock source ID - in production, this comes from Square SDK
      const mockSourceId = `cnon:card-nonce-${Date.now()}`;
      const mockPaymentId = `payment_${Date.now()}`;

      onPaymentSuccess(mockPaymentId, mockSourceId);
    } catch (error) {
      onPaymentError(error instanceof Error ? error.message : 'Payment processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  // Format card number (add spaces)
  const formatCardNumber = (value: string): string => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    }
    return v;
  };

  // Format expiry date (MM/YY)
  const formatExpiryDate = (value: string): string => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Input
          label="Cardholder Name"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="John Doe"
          required
          disabled={isProcessing || isLoading}
        />
      </div>

      <div>
        <Input
          label="Card Number"
          value={cardNumber}
          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
          placeholder="1234 5678 9012 3456"
          maxLength={19}
          required
          disabled={isProcessing || isLoading}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Input
            label="Expiry Date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
            placeholder="MM/YY"
            maxLength={5}
            required
            disabled={isProcessing || isLoading}
          />
        </div>
        <div>
          <Input
            label="CVV"
            type="password"
            value={cvv}
            onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').substring(0, 4))}
            placeholder="123"
            maxLength={4}
            required
            disabled={isProcessing || isLoading}
          />
        </div>
      </div>

      <div>
        <Input
          label="ZIP Code"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').substring(0, 10))}
          placeholder="12345"
          maxLength={10}
          required
          disabled={isProcessing || isLoading}
        />
      </div>

      <div className="pt-4">
        <Button
          type="submit"
          isLoading={isProcessing || isLoading}
          disabled={isProcessing || isLoading}
          className="w-full"
        >
          {isProcessing || isLoading ? 'Processing...' : `Pay ${(amount / 100).toFixed(2)}`}
        </Button>
      </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 text-center flex items-center justify-center gap-2">
        <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
        Your payment information is secure and encrypted
      </p>
    </form>
  );
}

